import { getLayerAttributes } from '@/api/layers';
import { isHiddenLayerAttribute } from '@/data/hiddenLayerAttributes';
import { createLayerTemplate, updateLayerTemplateProperties } from '@/api/layerTemplates';
import { useAuth } from '@/hooks/useAuth';
import { UserRole } from '@/types';
import { LayerTemplateCreate, LayerTemplatePropertyUpdate } from '@/types/layerTemplate';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Spreadsheet, {
  CellBase,
  DataViewerProps,
  Matrix,
  PointRange,
  RangeSelection,
  Selection,
} from 'react-spreadsheet';

interface LayerTemplateImportCsvModalProps {
  open: boolean;
  onClose: () => void;
  onTemplateCreated?: () => void;
}

const PUBLIC_STATUS_UUID = '53184dbd-ff42-497e-8137-a89dda5e5f87';
const COMPANY_PUBLIC_UUID = '20a7f07b-93c4-4188-93c1-920bbc8796c8';
const PRIVATE_UUID = '8e3819e8-4fb5-4eef-9bcb-188e9f7059e9';

interface ImportFormState {
  name: string;
  type: string;
  visibility_status_id: string;
}

const initialFormState: ImportFormState = {
  name: '',
  type: '',
  visibility_status_id: PRIVATE_UUID,
};

const SUPPORTED_DELIMITERS = [';', ',', '\t', '|'];
const AUTO_DELIMITER_MODE = 'auto';
type DelimiterMode = typeof AUTO_DELIMITER_MODE | ';' | ',' | '\t' | '|';

type Pair = {
  attribute: string;
  value: string;
};

type ParsedPairRow = {
  id: string;
  sourceAttribute: string;
  value: string;
  mappedAttributeId: string;
  autoSuggested: boolean;
  matchScore: number;
};

type SystemAttribute = {
  id: string;
  name: string;
  unit?: string | null;
};

type RangeValidationResult = {
  isValid: boolean;
  message?: string;
};

type MatchCandidate = {
  attributeId: string;
  score: number;
};

const parseCsvLine = (line: string, delimiter: string): string[] => {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = line[index + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === delimiter && !inQuotes) {
      cells.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells;
};

const detectDelimiter = (text: string): string => {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0).slice(0, 40);
  if (lines.length === 0) {
    return ';';
  }

  let bestDelimiter = ';';
  let bestScore = -1;

  SUPPORTED_DELIMITERS.forEach((delimiter) => {
    const counts = lines.map((line) => parseCsvLine(line, delimiter).filter((cell) => cell !== '').length);
    const maxCount = Math.max(...counts);
    const stableRows = counts.filter((count) => count > 1).length;
    const score = maxCount * 5 + stableRows;
    if (score > bestScore) {
      bestScore = score;
      bestDelimiter = delimiter;
    }
  });

  return bestDelimiter;
};

const toSpreadsheetData = (rows: string[][]): Matrix<CellBase<string>> => {
  const maxColumns = rows.reduce((max, row) => Math.max(max, row.length), 0);
  return rows.map((row) =>
    Array.from({ length: maxColumns }, (_, columnIndex) => ({
      value: row[columnIndex] ?? '',
    }))
  );
};

const detectHeaderRange = (rows: string[][]): { attributesRange: PointRange; valuesRange: PointRange } | null => {
  if (rows.length < 2) {
    return null;
  }

  let bestRow = -1;
  let bestCount = 0;

  rows.forEach((row, rowIndex) => {
    const nonEmptyColumns = row.reduce<number[]>((acc, cell, columnIndex) => {
      if (cell.trim() !== '') {
        acc.push(columnIndex);
      }
      return acc;
    }, []);
    if (nonEmptyColumns.length > bestCount) {
      bestCount = nonEmptyColumns.length;
      bestRow = rowIndex;
    }
  });

  if (bestRow < 0 || bestCount < 2 || bestRow + 1 >= rows.length) {
    return null;
  }

  const headerColumns = rows[bestRow]
    .map((cell, columnIndex) => ({ cell, columnIndex }))
    .filter((item) => item.cell.trim() !== '')
    .map((item) => item.columnIndex);

  const startColumn = Math.min(...headerColumns);
  const endColumn = Math.max(...headerColumns);

  return {
    attributesRange: new PointRange(
      { row: bestRow, column: startColumn },
      { row: bestRow, column: endColumn }
    ),
    valuesRange: new PointRange(
      { row: bestRow + 1, column: startColumn },
      { row: bestRow + 1, column: endColumn }
    ),
  };
};

const detectKeyValueColumns = (rows: string[][]): { attributesRange: PointRange; valuesRange: PointRange } | null => {
  if (rows.length < 3) {
    return null;
  }

  const maxColumns = rows.reduce((max, row) => Math.max(max, row.length), 0);
  let best: { column: number; score: number; startRow: number; endRow: number } | null = null;

  for (let col = 0; col < maxColumns - 1; col += 1) {
    let startRow = -1;
    let endRow = -1;
    let score = 0;

    rows.forEach((row, rowIndex) => {
      const left = (row[col] ?? '').trim();
      const right = (row[col + 1] ?? '').trim();
      if (left !== '' && right !== '') {
        if (startRow < 0) {
          startRow = rowIndex;
        }
        endRow = rowIndex;
        score += 1;
      }
    });

    if (score >= 4 && (!best || score > best.score)) {
      best = { column: col, score, startRow, endRow };
    }
  }

  if (!best || best.startRow < 0 || best.endRow < 0) {
    return null;
  }

  return {
    attributesRange: new PointRange(
      { row: best.startRow, column: best.column },
      { row: best.endRow, column: best.column }
    ),
    valuesRange: new PointRange(
      { row: best.startRow, column: best.column + 1 },
      { row: best.endRow, column: best.column + 1 }
    ),
  };
};

const extractRangeValues = (
  data: Matrix<CellBase<string>>,
  range: PointRange | null
): string[] => {
  if (!range) {
    return [];
  }

  const values: string[] = [];
  for (let row = range.start.row; row <= range.end.row; row += 1) {
    for (let column = range.start.column; column <= range.end.column; column += 1) {
      values.push((data[row]?.[column]?.value ?? '').toString().trim());
    }
  }
  return values;
};

const pointIsInRange = (row: number, column: number, range: PointRange | null): boolean => {
  if (!range) {
    return false;
  }
  return (
    row >= range.start.row &&
    row <= range.end.row &&
    column >= range.start.column &&
    column <= range.end.column
  );
};

const columnIndexToLetters = (columnIndex: number): string => {
  let value = columnIndex + 1;
  let result = '';
  while (value > 0) {
    const remainder = (value - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    value = Math.floor((value - 1) / 26);
  }
  return result;
};

const lettersToColumnIndex = (letters: string): number => {
  let result = 0;
  for (let index = 0; index < letters.length; index += 1) {
    result = result * 26 + (letters.charCodeAt(index) - 64);
  }
  return result - 1;
};

const formatRangeAddress = (range: PointRange | null): string => {
  if (!range) {
    return '';
  }
  const start = `${columnIndexToLetters(range.start.column)}${range.start.row + 1}`;
  const end = `${columnIndexToLetters(range.end.column)}${range.end.row + 1}`;
  return `${start}:${end}`;
};

const normalizeRange = (range: PointRange): PointRange => {
  const startRow = Math.min(range.start.row, range.end.row);
  const endRow = Math.max(range.start.row, range.end.row);
  const startColumn = Math.min(range.start.column, range.end.column);
  const endColumn = Math.max(range.start.column, range.end.column);
  return new PointRange({ row: startRow, column: startColumn }, { row: endRow, column: endColumn });
};

const parseRangeAddress = (input: string): PointRange | null => {
  const normalized = input.trim().toUpperCase();
  if (!normalized) {
    return null;
  }

  const [startRaw, endRaw] = normalized.includes(':')
    ? normalized.split(':')
    : [normalized, normalized];

  const parseCell = (cellAddress: string): { row: number; column: number } | null => {
    const match = cellAddress.trim().match(/^([A-Z]+)(\d+)$/);
    if (!match) {
      return null;
    }
    const column = lettersToColumnIndex(match[1]);
    const row = Number(match[2]) - 1;
    if (column < 0 || Number.isNaN(row) || row < 0) {
      return null;
    }
    return { row, column };
  };

  const startPoint = parseCell(startRaw);
  const endPoint = parseCell(endRaw);
  if (!startPoint || !endPoint) {
    return null;
  }

  return normalizeRange(new PointRange(startPoint, endPoint));
};

const rangeCellCount = (range: PointRange): number => {
  const rowCount = range.end.row - range.start.row + 1;
  const columnCount = range.end.column - range.start.column + 1;
  return rowCount * columnCount;
};

const isSingleRowRange = (range: PointRange): boolean => range.start.row === range.end.row;
const isSingleColumnRange = (range: PointRange): boolean => range.start.column === range.end.column;

const validateAttributeAndValueRanges = (
  attributesRange: PointRange | null,
  valuesRange: PointRange | null
): RangeValidationResult => {
  if (!attributesRange || !valuesRange) {
    return {
      isValid: false,
      message: 'Please select both attribute and value ranges before saving.',
    };
  }

  const attributes = normalizeRange(attributesRange);
  const values = normalizeRange(valuesRange);

  const attributesCount = rangeCellCount(attributes);
  const valuesCount = rangeCellCount(values);
  if (attributesCount !== valuesCount) {
    return {
      isValid: false,
      message: 'The number of attribute cells and value cells must be equal.',
    };
  }

  const attributesIsRow = isSingleRowRange(attributes);
  const attributesIsColumn = isSingleColumnRange(attributes);
  const valuesIsRow = isSingleRowRange(values);
  const valuesIsColumn = isSingleColumnRange(values);

  const attributesIs2D = !attributesIsRow && !attributesIsColumn;
  const valuesIs2D = !valuesIsRow && !valuesIsColumn;
  if (attributesIs2D || valuesIs2D) {
    return {
      isValid: false,
      message: 'Only one-dimensional ranges are supported (single row or single column).',
    };
  }

  // Row mode: same column bounds, different row allowed.
  if (attributesIsRow) {
    if (!valuesIsRow) {
      return {
        isValid: false,
        message: 'If attributes are selected in a row, values must also be selected in a row.',
      };
    }

    if (
      attributes.start.column !== values.start.column ||
      attributes.end.column !== values.end.column
    ) {
      return {
        isValid: false,
        message:
          'For row mode, attribute and value ranges must have the same column range.',
      };
    }

    return { isValid: true };
  }

  // Column mode: same row bounds, different column allowed.
  if (!valuesIsColumn) {
    return {
      isValid: false,
      message: 'If attributes are selected in a column, values must also be selected in a column.',
    };
  }

  if (
    attributes.start.row !== values.start.row ||
    attributes.end.row !== values.end.row
  ) {
    return {
      isValid: false,
      message:
        'For column mode, attribute and value ranges must have the same row range.',
    };
  }

  return { isValid: true };
};

const normalizeAttributeName = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[_\-\/\\|(),.;:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const tokenSetSimilarity = (left: string, right: string): number => {
  const leftTokens = new Set(left.split(' ').filter(Boolean));
  const rightTokens = new Set(right.split(' ').filter(Boolean));
  if (leftTokens.size === 0 || rightTokens.size === 0) {
    return 0;
  }

  let intersection = 0;
  leftTokens.forEach((token) => {
    if (rightTokens.has(token)) {
      intersection += 1;
    }
  });

  return intersection / Math.max(leftTokens.size, rightTokens.size);
};

const levenshteinDistance = (left: string, right: string): number => {
  const rows = left.length + 1;
  const cols = right.length + 1;
  const matrix: number[][] = Array.from({ length: rows }, () => Array(cols).fill(0));

  for (let row = 0; row < rows; row += 1) {
    matrix[row][0] = row;
  }
  for (let col = 0; col < cols; col += 1) {
    matrix[0][col] = col;
  }

  for (let row = 1; row < rows; row += 1) {
    for (let col = 1; col < cols; col += 1) {
      const cost = left[row - 1] === right[col - 1] ? 0 : 1;
      matrix[row][col] = Math.min(
        matrix[row - 1][col] + 1,
        matrix[row][col - 1] + 1,
        matrix[row - 1][col - 1] + cost
      );
    }
  }

  return matrix[left.length][right.length];
};

const levenshteinSimilarity = (left: string, right: string): number => {
  if (!left && !right) {
    return 1;
  }
  const maxLength = Math.max(left.length, right.length);
  if (maxLength === 0) {
    return 1;
  }
  return 1 - levenshteinDistance(left, right) / maxLength;
};

const charBigramSimilarity = (left: string, right: string): number => {
  const toBigrams = (value: string): string[] => {
    if (value.length < 2) {
      return value ? [value] : [];
    }
    const result: string[] = [];
    for (let index = 0; index < value.length - 1; index += 1) {
      result.push(value.slice(index, index + 2));
    }
    return result;
  };

  const leftBigrams = toBigrams(left);
  const rightBigrams = toBigrams(right);
  if (leftBigrams.length === 0 || rightBigrams.length === 0) {
    return 0;
  }

  const leftCounts = new Map<string, number>();
  leftBigrams.forEach((bigram) => {
    leftCounts.set(bigram, (leftCounts.get(bigram) ?? 0) + 1);
  });

  let intersection = 0;
  rightBigrams.forEach((bigram) => {
    const current = leftCounts.get(bigram) ?? 0;
    if (current > 0) {
      intersection += 1;
      leftCounts.set(bigram, current - 1);
    }
  });

  return (2 * intersection) / (leftBigrams.length + rightBigrams.length);
};

const compositeSimilarity = (leftRaw: string, rightRaw: string): number => {
  const left = normalizeAttributeName(leftRaw);
  const right = normalizeAttributeName(rightRaw);
  if (!left || !right) {
    return 0;
  }
  if (left === right) {
    return 1;
  }

  const token = tokenSetSimilarity(left, right);
  const lev = levenshteinSimilarity(left, right);
  const bigram = charBigramSimilarity(left, right);

  return 0.45 * token + 0.3 * lev + 0.25 * bigram;
};

const findBestAttributeMatch = (
  sourceAttribute: string,
  attributes: SystemAttribute[]
): MatchCandidate | null => {
  let best: MatchCandidate | null = null;

  attributes.forEach((attribute) => {
    const score = compositeSimilarity(sourceAttribute, attribute.name);
    if (!best || score > best.score) {
      best = { attributeId: attribute.id, score };
    }
  });

  return best;
};

export const LayerTemplateImportCsvModal: React.FC<LayerTemplateImportCsvModalProps> = ({
  open,
  onClose,
  onTemplateCreated,
}) => {
  const { user } = useAuth();
  const isSuperAdmin = useMemo(
    () => user?.role === UserRole.ADMIN,
    [user?.role]
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const parsedPairsRef = useRef<HTMLDivElement | null>(null);
  const [formData, setFormData] = useState<ImportFormState>(initialFormState);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [rawCsvContent, setRawCsvContent] = useState('');
  const [spreadsheetData, setSpreadsheetData] = useState<Matrix<CellBase<string>>>([]);
  const [selectedRange, setSelectedRange] = useState<PointRange | null>(null);
  const [selection, setSelection] = useState<Selection | undefined>(undefined);
  const [attributesRange, setAttributesRange] = useState<PointRange | null>(null);
  const [valuesRange, setValuesRange] = useState<PointRange | null>(null);
  const [detectedDelimiter, setDetectedDelimiter] = useState(';');
  const [delimiterMode, setDelimiterMode] = useState<DelimiterMode>(AUTO_DELIMITER_MODE);
  const [error, setError] = useState<string | null>(null);
  const [rangeWarning, setRangeWarning] = useState<string | null>(null);
  const [templateSaveWarning, setTemplateSaveWarning] = useState<string | null>(null);
  const [savedPairs, setSavedPairs] = useState<ParsedPairRow[]>([]);
  const [attributesRangeInput, setAttributesRangeInput] = useState('');
  const [valuesRangeInput, setValuesRangeInput] = useState('');
  const [systemAttributes, setSystemAttributes] = useState<SystemAttribute[]>([]);
  const [systemAttributesLoading, setSystemAttributesLoading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);

  const visibleSystemAttributes = useMemo(
    () => systemAttributes.filter((attribute) => !isHiddenLayerAttribute(attribute.id, attribute.name)),
    [systemAttributes],
  );

  const visibleSavedPairs = useMemo(
    () => savedPairs.filter((pair) => {
      if (isHiddenLayerAttribute(pair.mappedAttributeId)) {
        return false;
      }
      if (isHiddenLayerAttribute(null, pair.sourceAttribute)) {
        return false;
      }
      return true;
    }),
    [savedPairs],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    const loadSystemAttributes = async () => {
      try {
        setSystemAttributesLoading(true);
        const attributes = await getLayerAttributes();
        setSystemAttributes(attributes);
      } catch (loadError) {
        console.error('Failed to load system attributes:', loadError);
      } finally {
        setSystemAttributesLoading(false);
      }
    };

    loadSystemAttributes();
  }, [open]);

  const resetState = () => {
    setFormData(initialFormState);
    setSelectedFileName('');
    setRawCsvContent('');
    setSpreadsheetData([]);
    setSelectedRange(null);
    setSelection(undefined);
    setAttributesRange(null);
    setValuesRange(null);
    setDetectedDelimiter(';');
    setDelimiterMode(AUTO_DELIMITER_MODE);
    setError(null);
    setRangeWarning(null);
    setTemplateSaveWarning(null);
    setSavedPairs([]);
    setAttributesRangeInput('');
    setValuesRangeInput('');
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleOpenFileDialog = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv';
    if (!isCsv) {
      setError('Only CSV files are supported for import');
      setSelectedFileName('');
      setSpreadsheetData([]);
      setSelection(undefined);
      setSelectedRange(null);
      setAttributesRange(null);
      setValuesRange(null);
      return;
    }

    try {
      const text = await file.text();
      setSelectedFileName(file.name);
      setRawCsvContent(text);
      setDelimiterMode(AUTO_DELIMITER_MODE);

      const delimiter = detectDelimiter(text);
      const rows = text
        .split(/\r?\n/)
        .map((line) => parseCsvLine(line, delimiter))
        .filter((row) => row.some((cell) => cell.trim() !== ''));

      if (rows.length === 0) {
        setError('CSV file is empty');
        return;
      }

      const matrix = toSpreadsheetData(rows);
      const keyValueGuess = detectKeyValueColumns(rows);
      const headerGuess = detectHeaderRange(rows);
      const initialRanges = keyValueGuess ?? headerGuess;

      setDetectedDelimiter(delimiter);
      setSpreadsheetData(matrix);
      setAttributesRange(initialRanges?.attributesRange ?? null);
      setValuesRange(initialRanges?.valuesRange ?? null);
      setAttributesRangeInput(formatRangeAddress(initialRanges?.attributesRange ?? null));
      setValuesRangeInput(formatRangeAddress(initialRanges?.valuesRange ?? null));
      setSelection(initialRanges ? new RangeSelection(initialRanges.attributesRange) : undefined);
      setSelectedRange(initialRanges?.attributesRange ?? null);
      setError(null);
      setRangeWarning(null);
      setTemplateSaveWarning(null);
      setSavedPairs([]);
    } catch (readError) {
      console.error('Failed to read CSV file:', readError);
      setError('Failed to read file content');
      setSelectedFileName('');
      setSpreadsheetData([]);
    }
  };

  const reparseWithDelimiterMode = (nextMode: DelimiterMode) => {
    if (!rawCsvContent) {
      setDelimiterMode(nextMode);
      return;
    }

    const delimiter = nextMode === AUTO_DELIMITER_MODE
      ? detectDelimiter(rawCsvContent)
      : nextMode;

    const rows = rawCsvContent
      .split(/\r?\n/)
      .map((line) => parseCsvLine(line, delimiter))
      .filter((row) => row.some((cell) => cell.trim() !== ''));

    if (rows.length === 0) {
      setError('CSV file is empty for selected delimiter');
      setSpreadsheetData([]);
      setSelection(undefined);
      setSelectedRange(null);
      setAttributesRange(null);
      setValuesRange(null);
      setAttributesRangeInput('');
      setValuesRangeInput('');
      setSavedPairs([]);
      setRangeWarning(null);
      setTemplateSaveWarning(null);
      setDelimiterMode(nextMode);
      setDetectedDelimiter(delimiter);
      return;
    }

    const matrix = toSpreadsheetData(rows);
    const keyValueGuess = detectKeyValueColumns(rows);
    const headerGuess = detectHeaderRange(rows);
    const initialRanges = keyValueGuess ?? headerGuess;

    setDelimiterMode(nextMode);
    setDetectedDelimiter(delimiter);
    setSpreadsheetData(matrix);
    setAttributesRange(initialRanges?.attributesRange ?? null);
    setValuesRange(initialRanges?.valuesRange ?? null);
    setAttributesRangeInput(formatRangeAddress(initialRanges?.attributesRange ?? null));
    setValuesRangeInput(formatRangeAddress(initialRanges?.valuesRange ?? null));
    setSelection(initialRanges ? new RangeSelection(initialRanges.attributesRange) : undefined);
    setSelectedRange(initialRanges?.attributesRange ?? null);
    setSavedPairs([]);
    setRangeWarning(null);
    setTemplateSaveWarning(null);
    setError(null);
  };

  const handleSelectionChange = (nextSelection: Selection) => {
    setSelection(nextSelection);
    const range = nextSelection.toRange(spreadsheetData);
    // Keep the last concrete selected range so assignment buttons
    // still work when spreadsheet clears selection on blur/click.
    if (range) {
      setSelectedRange(normalizeRange(range));
    }
  };

  const assignSelectedRangeToKind = (kind: 'attributes' | 'values') => {
    if (!selectedRange) {
      return;
    }

    const normalized = normalizeRange(selectedRange);
    const formatted = formatRangeAddress(normalized);

    if (kind === 'attributes') {
      setAttributesRange(normalized);
      setAttributesRangeInput(formatted);
    } else {
      setValuesRange(normalized);
      setValuesRangeInput(formatted);
    }

    setSelection(new RangeSelection(normalized));
    setSelectedRange(normalized);
    setRangeWarning(null);
    setSavedPairs([]);
  };

  const resolveRangesForSave = (): {
    attributesRange: PointRange | null;
    valuesRange: PointRange | null;
  } => {
    const attributesFromInput = attributesRangeInput.trim()
      ? parseRangeAddress(attributesRangeInput)
      : null;
    const valuesFromInput = valuesRangeInput.trim()
      ? parseRangeAddress(valuesRangeInput)
      : null;

    return {
      attributesRange: attributesFromInput ?? attributesRange,
      valuesRange: valuesFromInput ?? valuesRange,
    };
  };

  const handleSaveRanges = () => {
    const resolved = resolveRangesForSave();
    const validation = validateAttributeAndValueRanges(
      resolved.attributesRange,
      resolved.valuesRange
    );
    if (!validation.isValid) {
      setRangeWarning(validation.message ?? 'Selected ranges are not valid.');
      setTemplateSaveWarning(null);
      setSavedPairs([]);
      return;
    }

    const normalizedAttributes = normalizeRange(resolved.attributesRange as PointRange);
    const normalizedValues = normalizeRange(resolved.valuesRange as PointRange);
    setAttributesRange(normalizedAttributes);
    setValuesRange(normalizedValues);
    setAttributesRangeInput(formatRangeAddress(normalizedAttributes));
    setValuesRangeInput(formatRangeAddress(normalizedValues));

    setRangeWarning(null);
    setTemplateSaveWarning(null);
    const attributes = extractRangeValues(spreadsheetData, normalizedAttributes);
    const values = extractRangeValues(spreadsheetData, normalizedValues);
    const length = Math.min(attributes.length, values.length);
    const pairs: Pair[] = Array.from({ length }, (_, index) => ({
      attribute: attributes[index] ?? '',
      value: values[index] ?? '',
    }));

    const rows: ParsedPairRow[] = pairs.map((pair, index) => ({
      id: `${Date.now()}-${index}`,
      sourceAttribute: pair.attribute,
      value: pair.value,
      mappedAttributeId: '',
      autoSuggested: false,
      matchScore: 0,
    }));

    // Auto-suggest mapping for similar names; user can edit mapping manually.
    const AUTO_MAP_THRESHOLD = 0.78;
    rows.forEach((row) => {
      const bestMatch = findBestAttributeMatch(row.sourceAttribute, visibleSystemAttributes);
      if (bestMatch && bestMatch.score >= AUTO_MAP_THRESHOLD) {
        row.mappedAttributeId = bestMatch.attributeId;
        row.autoSuggested = true;
        row.matchScore = bestMatch.score;
      }
    });

    setSavedPairs(rows);
    window.requestAnimationFrame(() => {
      parsedPairsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const applyRangeInput = (kind: 'attributes' | 'values') => {
    const text = kind === 'attributes' ? attributesRangeInput : valuesRangeInput;
    if (!text.trim()) {
      if (kind === 'attributes') {
        setAttributesRange(null);
      } else {
        setValuesRange(null);
      }
      setRangeWarning(null);
      setSavedPairs([]);
      return;
    }

    const parsed = parseRangeAddress(text);

    if (!parsed) {
      setRangeWarning('Invalid range format. Use A1:B10 format.');
      setSavedPairs([]);
      return;
    }

    if (kind === 'attributes') {
      setAttributesRange(parsed);
      setAttributesRangeInput(formatRangeAddress(parsed));
      setSelection(new RangeSelection(parsed));
      setSelectedRange(parsed);
    } else {
      setValuesRange(parsed);
      setValuesRangeInput(formatRangeAddress(parsed));
      setSelection(new RangeSelection(parsed));
      setSelectedRange(parsed);
    }

    setRangeWarning(null);
    setSavedPairs([]);
  };

  const handleRangeInputKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    kind: 'attributes' | 'values'
  ) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyRangeInput(kind);
    }
  };

  const HighlightedDataViewer: React.FC<DataViewerProps<CellBase<string>>> = ({ cell, row, column }) => {
    const inAttributesRange = pointIsInRange(row, column, attributesRange);
    const inValuesRange = pointIsInRange(row, column, valuesRange);

    let backgroundColor = 'transparent';
    if (inAttributesRange && inValuesRange) {
      backgroundColor = 'rgba(156, 39, 176, 0.22)';
    } else if (inAttributesRange) {
      backgroundColor = 'rgba(25, 118, 210, 0.18)';
    } else if (inValuesRange) {
      backgroundColor = 'rgba(46, 125, 50, 0.18)';
    }

    return (
      <Box sx={{ minHeight: 22, px: 0.75, py: 0.25, backgroundColor }}>
        {cell?.value ?? ''}
      </Box>
    );
  };

  const handlePairValueChange = (rowId: string, value: string) => {
    setSavedPairs((prev) =>
      prev.map((row) => (row.id === rowId ? { ...row, value } : row))
    );
  };

  const handlePairMappedAttributeChange = (rowId: string, mappedAttributeId: string) => {
    setSavedPairs((prev) =>
      prev.map((row) =>
        row.id === rowId
          ? { ...row, mappedAttributeId, autoSuggested: false }
          : row
      )
    );
  };

  const parseNumericValue = (value: string): number | null => {
    const normalized = value.trim();
    if (!normalized) {
      return null;
    }
    const parsed = Number(normalized.replace(',', '.'));
    if (!Number.isFinite(parsed)) {
      return Number.NaN;
    }
    return parsed;
  };

  const validateBeforeTemplateSave = (): string | null => {
    if (!formData.name.trim()) {
      return 'Template name is required.';
    }
    if (!savedPairs.length) {
      return 'Please save parsed pairs first.';
    }
    if (!systemAttributes.length) {
      return 'System attributes are not loaded yet.';
    }

    const mappedRows = savedPairs.filter((row) => row.mappedAttributeId);
    const mappedIds = mappedRows.map((row) => row.mappedAttributeId);
    const uniqueMappedIds = new Set(mappedIds);
    if (uniqueMappedIds.size !== mappedIds.length) {
      return 'One system attribute cannot be used for multiple parsed pairs.';
    }

    for (const row of mappedRows) {
      const parsed = parseNumericValue(row.value);
      if (Number.isNaN(parsed)) {
        const source = row.sourceAttribute || 'Unnamed attribute';
        return `Value for "${source}" must be numeric or empty.`;
      }
    }

    const hasAtLeastOneNumeric = mappedRows.some((row) => {
      const parsed = parseNumericValue(row.value);
      return typeof parsed === 'number' && !Number.isNaN(parsed);
    });
    if (!hasAtLeastOneNumeric) {
      return 'At least one mapped attribute must contain a numeric value.';
    }

    return null;
  };

  const handleSaveTemplate = async () => {
    const validationError = validateBeforeTemplateSave();
    if (validationError) {
      setTemplateSaveWarning(validationError);
      window.requestAnimationFrame(() => {
        parsedPairsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      return;
    }

    setSavingTemplate(true);
    setError(null);
    setTemplateSaveWarning(null);

    try {
      const templatePayload: LayerTemplateCreate = {
        name: formData.name.trim(),
        type: formData.type || '',
        is_public: formData.visibility_status_id === PUBLIC_STATUS_UUID,
        visibility_status_id: formData.visibility_status_id,
      };

      const createdTemplate = await createLayerTemplate(templatePayload);

      const mappedByAttributeId = new Map<string, ParsedPairRow>();
      savedPairs.forEach((row) => {
        if (row.mappedAttributeId) {
          mappedByAttributeId.set(row.mappedAttributeId, row);
        }
      });

      const propertiesPayload: LayerTemplatePropertyUpdate[] = systemAttributes.map((attribute) => {
        const mappedRow = mappedByAttributeId.get(attribute.id);
        if (!mappedRow) {
          return { attribute_id: attribute.id, value: null };
        }

        const parsed = parseNumericValue(mappedRow.value);
        return {
          attribute_id: attribute.id,
          value: parsed === null || Number.isNaN(parsed) ? null : parsed,
        };
      });

      await updateLayerTemplateProperties(createdTemplate.id, propertiesPayload);
      onTemplateCreated?.();
      handleClose();
    } catch (saveError) {
      console.error('Failed to save template from CSV import:', saveError);
      setTemplateSaveWarning(
        saveError instanceof Error ? saveError.message : 'Failed to save template'
      );
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Import Reservoir Template from CSV</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 3, mt: 0.5 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Template Name"
              value={formData.name}
              onChange={(event) => setFormData((prev) => ({ ...prev, name: event.target.value }))}
              required
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                label="Type"
                onChange={(event) => setFormData((prev) => ({ ...prev, type: event.target.value }))}
              >
                <MenuItem value="">Not specified</MenuItem>
                <MenuItem value="oil">Oil</MenuItem>
                <MenuItem value="gas">Gas</MenuItem>
                <MenuItem value="oil_and_gas">Oil &amp; Gas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={formData.visibility_status_id}
                label="Visibility"
                onChange={(event) =>
                  setFormData((prev) => ({
                    ...prev,
                    visibility_status_id: event.target.value as string,
                  }))
                }
              >
                <MenuItem value={PRIVATE_UUID}>Private</MenuItem>
                <MenuItem value={COMPANY_PUBLIC_UUID}>Company Public</MenuItem>
                {isSuperAdmin && <MenuItem value={PUBLIC_STATUS_UUID}>Public</MenuItem>}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" onClick={handleOpenFileDialog}>
              Upload CSV
            </Button>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Delimiter</InputLabel>
              <Select
                value={delimiterMode}
                label="Delimiter"
                onChange={(event) => reparseWithDelimiterMode(event.target.value as DelimiterMode)}
                disabled={!rawCsvContent}
              >
                <MenuItem value={AUTO_DELIMITER_MODE}>Auto</MenuItem>
                <MenuItem value=";">Semicolon (;)</MenuItem>
                <MenuItem value=",">Comma (,)</MenuItem>
                <MenuItem value={'\t'}>Tab (\t)</MenuItem>
                <MenuItem value="|">Pipe (|)</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          {selectedFileName && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Selected file: {selectedFileName} (delimiter: "{detectedDelimiter === '\t' ? '\\t' : detectedDelimiter}")
            </Typography>
          )}
        </Box>

        {spreadsheetData.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Mapping setup
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Attributes range"
                  placeholder="e.g. A8:Z8"
                  value={attributesRangeInput}
                  onChange={(event) => setAttributesRangeInput(event.target.value)}
                  onBlur={() => applyRangeInput('attributes')}
                  onKeyDown={(event) => handleRangeInputKeyDown(event, 'attributes')}
                  helperText="Example: A8:Z8"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Values range"
                  placeholder="e.g. A9:Z9"
                  value={valuesRangeInput}
                  onChange={(event) => setValuesRangeInput(event.target.value)}
                  onBlur={() => applyRangeInput('values')}
                  onKeyDown={(event) => handleRangeInputKeyDown(event, 'values')}
                  helperText="Example: A9:Z9"
                />
              </Grid>
            </Grid>

            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                disabled={!selectedRange}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => assignSelectedRangeToKind('attributes')}
              >
                Use selection as Attributes
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={!selectedRange}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => assignSelectedRangeToKind('values')}
              >
                Use selection as Values
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSaveRanges}
                disabled={
                  !attributesRange
                  && !valuesRange
                  && !attributesRangeInput.trim()
                  && !valuesRangeInput.trim()
                }
              >
                Save
              </Button>
            </Box>
            {rangeWarning && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {rangeWarning}
              </Alert>
            )}

            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Select any range in the table
            </Typography>
            <Box
              sx={{
                overflow: 'auto',
                maxHeight: 360,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '& table': {
                  borderCollapse: 'separate',
                  borderSpacing: 0,
                },
                '& table tr:first-of-type th': {
                  position: 'sticky',
                  top: 0,
                  zIndex: 4,
                  backgroundColor: 'background.paper',
                },
                // Keep only row index column sticky (do not pin first data column)
                '& table tbody tr th:first-of-type': {
                  position: 'sticky',
                  left: 0,
                  zIndex: 3,
                  backgroundColor: 'background.paper',
                },
                '& table tr:first-of-type th:first-of-type': {
                  zIndex: 5,
                },
              }}
            >
              <Spreadsheet
                data={spreadsheetData}
                selected={selection}
                onSelect={handleSelectionChange}
                DataViewer={HighlightedDataViewer}
              />
            </Box>

            <Divider sx={{ my: 2 }} />
            <Box ref={parsedPairsRef}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Parsed attribute-value pairs
              </Typography>
            </Box>
            {templateSaveWarning && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {templateSaveWarning}
              </Alert>
            )}
            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '30%' }}>Parsed attribute</TableCell>
                    <TableCell sx={{ width: '30%' }}>Value</TableCell>
                    <TableCell sx={{ width: '40%' }}>System attribute</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleSavedPairs.length > 0 ? (
                    visibleSavedPairs.map((pair) => (
                      <TableRow key={pair.id}>
                        <TableCell>{pair.sourceAttribute || '-'}</TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={pair.value}
                            onChange={(event) => handlePairValueChange(pair.id, event.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={pair.mappedAttributeId}
                              onChange={(event) =>
                                handlePairMappedAttributeChange(pair.id, event.target.value as string)
                              }
                              displayEmpty
                              disabled={systemAttributesLoading}
                            >
                              <MenuItem value="">
                                <em>Not mapped</em>
                              </MenuItem>
                              {visibleSystemAttributes.map((attribute) => (
                                <MenuItem key={attribute.id} value={attribute.id}>
                                  {attribute.name}
                                  {attribute.unit ? ` (${attribute.unit})` : ''}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {pair.autoSuggested && pair.mappedAttributeId && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              Auto-mapped ({Math.round(pair.matchScore * 100)}%)
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ color: 'text.secondary' }}>
                        Select ranges and click Save to show parsed pairs.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {systemAttributesLoading && (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading system attributes...
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={savingTemplate}>Close</Button>
        <Button
          variant="contained"
          onClick={handleSaveTemplate}
          disabled={savingTemplate || systemAttributesLoading}
        >
          {savingTemplate ? 'Saving...' : 'Save template'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LayerTemplateImportCsvModal;
